import type {DragEvent, ReactElement} from 'react';
import type {Block, Component} from 'grapesjs';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {useGrapesjsEditor, useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import {appendBlockOnClick} from '@EmailMakerReact/lib/email-maker-block-insert-library';
import {useAppDispatch} from '@/hooks';
import {setSelectedBlockPanel} from '@Components/email-maker-block-manager/email-maker-block-manager-reducer';
import {POPUP_PANEL_ID} from '@Components/email-maker-block-manager';
import {noop} from '@Utils/general.util';
import styles from './email-maker-block.module.scss';

interface EmailMakerBlockProps {
  block: Block;
  children?: ReactElement<any> | ReactElement<any>[];
  extraClasses?: string;
  onAddToCanvas?: (addedComponent: Component) => void;
  shouldAppendBlockOnClick?: boolean;
  onClick?: () => void;
}

const responsiveStyles = ['width'];

export function EmailMakerBlock({
  block,
  children,
  extraClasses = '',
  onAddToCanvas = noop,
  shouldAppendBlockOnClick = true,
  onClick = noop,
}: EmailMakerBlockProps): ReactElement<any> {
  const gjsEditor = useGrapesjsEditor();
  const isMobileScreen = useIsMobileScreen();
  const blockManager = gjsEditor.BlockManager;
  const dispatch = useAppDispatch();

  const clickHandler = (): void => {
    onClick();

    let addedComponent = null;
    if (shouldAppendBlockOnClick) {
      addedComponent = appendBlockOnClick(gjsEditor, block);

      if (!addedComponent) {
        return;
      }

      addMediaQueries(addedComponent);
    }

    // Must stay after appendBlockOnClick: media blocks open the stock popup during activate and read the still-open panel from Redux for their Back target.
    dispatch(setSelectedBlockPanel(null));

    if (isMobileScreen) {
      gjsEditor.runCommand(window.PMW.emails.commands.CLOSE_ADD_BLOCKS_DIALOG);
    }

    if (addedComponent) {
      gjsEditor.runCommand(window.PMW.emails.commands.SCROLL_TO_COMPONENT, {component: addedComponent, delay: 300});
      onAddToCanvas(addedComponent);
    }
  };

  const hidePanel = (): void => {
    const popup = document.getElementById(POPUP_PANEL_ID);
    popup?.classList.add('hiddenPanel');
  };

  const addMediaQueries = (component: Component): void => {
    gjsEditor.runCommand(window.PMW.emails.commands.REFRESH_MEDIA_QUERIES, {
      component,
      responsiveStyles: responsiveStyles,
    });
  };

  const dragStartHandler = (event: DragEvent<HTMLDivElement>): void => {
    if (isMobileScreen) {
      // Cancel a dragstart bubbled up from the inner native-draggable visual so touch scroll works.
      event.preventDefault();
      return;
    }

    hidePanel();
    event.dataTransfer.setDragImage(event.currentTarget, 0, 0);
    blockManager.startDrag(block, event.nativeEvent);
  };

  const dragEndHandler = (): void => {
    blockManager.endDrag(false);
    dispatch(setSelectedBlockPanel(null));

    const addedComponent = gjsEditor.getSelected();

    if (!addedComponent) {
      return;
    }

    addMediaQueries(addedComponent);
    onAddToCanvas(addedComponent);
  };

  return (
    <ClickableDiv
      key={block.getId()}
      className={`${styles.block} ${extraClasses}`}
      onClick={clickHandler}
      draggable={!isMobileScreen}
      onDragStart={dragStartHandler}
      onDragEnd={dragEndHandler}
    >
      {children}
    </ClickableDiv>
  );
}
